import { isValidTab } from '@shared/extension/utils/tabs';
import StudentWorksmartData from '../models/student-worksmart-data';
import { TimecardEvent } from '../models/timecard-event';
import { TimecardEventType } from '../models/timecard-event-type';
import Logger from '../utils/logger';
import { getFormattedCurrentTimestampInUTC, getFormattedTimestampInUTC } from '../utils/time-utils';
import { PersistenceService } from './persistence-service';
import { TimecardUploadService } from './timecard-upload-service';

import IdleState = chrome.idle.IdleState

const TIMECARD_DURATION_IN_MILLIS = 1000 * 60 * 10
const MILLIS_IN_ONE_SECOND = 1000
const ACTIVITY_LEVEL_ACTIVE_UPPER_BOUND = 10
const STEPS_PER_CYCLE = 4

const IDLE_DETECTION_INTERVAL_IN_SECONDS = 15

export class TimecardTrackingService {
  private readonly tabEventCallback = async () => await this.trackActiveTab()
  private _isTrackingEnabled = false
  private activityStatus?: IdleState
  private userTrackingMetadata?: StudentWorksmartData
  private idleTimer: NodeJS.Timeout | null = null
  private cycleStepCount: number = 0
  private activeCount: number = 0
  private mouseCount: number = 0
  private keyboardCount: number = 0

  private readonly setActivityStatus = async (currentStatus: IdleState): Promise<void> => {
    this.cycleStepCount += 1
    if (currentStatus === 'active') {
      this.activeCount += 1
      this.mouseCount += ACTIVITY_LEVEL_ACTIVE_UPPER_BOUND
      this.keyboardCount += ACTIVITY_LEVEL_ACTIVE_UPPER_BOUND
    }

    if (this.cycleStepCount === STEPS_PER_CYCLE) {
      this.activityStatus = this.activeCount > 1 ? 'active' : currentStatus
      const tab = await this.getCurrentTab()
      const url = tab?.url ?? ''
      const title = tab?.title ?? ''
      await this.trackActiveTab()
      await this.storeTimeCard({
        type: TimecardEventType.ACTIVITY,
        created_at: getFormattedCurrentTimestampInUTC(),
        is_random: false,
        mouse_count: (this.activityStatus === 'active' ? this.mouseCount : 0),
        keyboard_count: (this.activityStatus === 'active' ? this.keyboardCount : 0),
        keylog: '',
        mem_usage: 0,
        cpu_usage: 0,
        current_window: title.substring(0, 250),
        visiting_url: url.substring(0, 250),
        process_name: url.split('/')[2] ?? '',
        battery_percent: 0,
        battery_is_charging: false
      })
      this.mouseCount = 0
      this.keyboardCount = 0
      this.cycleStepCount = 0
      this.activeCount = 0
    }
  }

  constructor (private readonly persistenceService: PersistenceService = new PersistenceService(),
    private readonly timecardUploadService: TimecardUploadService = new TimecardUploadService(),
    private readonly logger = new Logger('TimecardTrackingService')) {
  }

  private addListeners () {
    chrome.tabs.onActivated.addListener(this.tabEventCallback)
    chrome.tabs.onUpdated.addListener(this.tabEventCallback)
    chrome.tabs.onCreated.addListener(this.tabEventCallback)
  }

  private removeListeners () {
    chrome.tabs.onActivated.removeListener(this.tabEventCallback)
    chrome.tabs.onUpdated.removeListener(this.tabEventCallback)
    chrome.tabs.onCreated.removeListener(this.tabEventCallback)
  }

  private async getCurrentTab (): Promise<chrome.tabs.Tab> {
    const queryOptions = { active: true, lastFocusedWindow: true }
    const [tab] = await chrome.tabs.query(queryOptions)
    return tab
  }

  private async storeTimeCard (event: TimecardEvent, triggerUploads = true): Promise<void> {
    setTimeout(async () => {
      await this.saveEvent(event, triggerUploads)
    }, MILLIS_IN_ONE_SECOND)
  }

  private triggerTimecardUploads (): void {
    setTimeout(async () => {
      await this.timecardUploadService.uploadTimecards(this.getBeginningTimeOfCurrentTimecard())
    }, MILLIS_IN_ONE_SECOND)
  }

  public startTracking (userTrackingMetadata: StudentWorksmartData) {
    this.stopTracking()
    this.logger.log(`Starting tracking for user ${userTrackingMetadata.userId}`)
    this._isTrackingEnabled = true
    this.userTrackingMetadata = userTrackingMetadata
    this.idleTimer = setInterval(() => {
      chrome.idle.queryState(IDLE_DETECTION_INTERVAL_IN_SECONDS, this.setActivityStatus)
    }, IDLE_DETECTION_INTERVAL_IN_SECONDS * 1000)

    this.addListeners()
    this.trackActiveTab().then(() => {
    })
  }

  public stopTracking (): void {
    if (!this.isTrackingEnabled) {
      return
    }
    this.logger.log('Stopping tracking')
    this.removeListeners()
    if (this.idleTimer != null) {
      clearInterval(this.idleTimer)
      this.idleTimer = null
    }

    this.triggerTimecardUploads()

    this._isTrackingEnabled = false
    this.mouseCount = 0
    this.keyboardCount = 0
    this.cycleStepCount = 0
    this.activeCount = 0
  }

  public get isTrackingEnabled (): boolean {
    return this._isTrackingEnabled
  }

  getBeginningTimeOfCurrentTimecard (): number {
    const currentTime = new Date().getTime()
    return (currentTime - (currentTime % TIMECARD_DURATION_IN_MILLIS))
  }

  async saveEvent (event: TimecardEvent, triggerUploads = true): Promise<void> {
    this.logger.debug(`Saving event: ${JSON.stringify(event)}`)
    const timecardId = this.getBeginningTimeOfCurrentTimecard()
    await this.persistenceService.createEvent({
      timecardId,
      userId: this.userTrackingMetadata!.userId,
      companyId: this.userTrackingMetadata!.companyId,
      teamId: this.userTrackingMetadata!.teamId,
      createdAt: getFormattedTimestampInUTC(timecardId)
    }, event)
    if (triggerUploads) {
      this.triggerTimecardUploads()
    }
  }

  private async trackActiveTab (): Promise<void> {
    if (!this.isTrackingEnabled) {
      return
    }

    const tab = await this.getCurrentTab()
    if (isValidTab(tab) && tab?.title && tab?.url) {
      await this.storeUrlActivityForTab(tab.title, tab.url)
    }
  }

  private async storeUrlActivityForTab (tabTitle: string, tabUrl: string): Promise<void> {
    await this.storeTimeCard({
      type: TimecardEventType.URL_ACTIVITY,
      created_at: getFormattedCurrentTimestampInUTC(),
      current_window: tabTitle.substring(0, 250),
      visiting_url: tabUrl.substring(0, 250),
      process_name: tabUrl.split('/')[2] ?? ''
    }, false)
  }

}
